/**
 * "Invite 10 learners" → "Invited 10 learners". Turns a proposal's title (an
 * imperative, by the contract's examples) into the assistant's done line.
 * Only the first word changes; a title that doesn't start with a verb we know
 * is returned as it is, which still reads as a summary of what was done.
 */

const IRREGULAR: Record<string, string> = {
  send: 'sent',
  set: 'set',
  make: 'made',
  build: 'built',
  put: 'put',
  write: 'wrote',
  rewrite: 'rewrote',
  run: 'ran',
  give: 'gave',
  get: 'got',
  shorten: 'shortened',
  open: 'opened',
};

const VERBS = new Set([
  'add',
  'apply',
  'archive',
  'assign',
  'change',
  'clone',
  'connect',
  'copy',
  'create',
  'delete',
  'duplicate',
  'edit',
  'enrol',
  'enroll',
  'generate',
  'import',
  'insert',
  'invite',
  'link',
  'lock',
  'move',
  'publish',
  'remind',
  'remove',
  'rename',
  'reorder',
  'replace',
  'reset',
  'restore',
  'schedule',
  'sequence',
  'unlock',
  'unpublish',
  'update',
  'upload',
  ...Object.keys(IRREGULAR),
]);

const DOUBLED = new Set(['enrol']);

function past(verb: string): string {
  if (IRREGULAR[verb]) return IRREGULAR[verb];
  if (DOUBLED.has(verb)) return `${verb}led`;
  if (verb.endsWith('e')) return `${verb}d`;
  if (/[^aeiou]y$/.test(verb)) return `${verb.slice(0, -1)}ied`;
  return `${verb}ed`;
}

export function pastTense(title: string): string {
  const match = /^(\s*)([A-Za-z]+)(.*)$/s.exec(title ?? '');
  if (!match) return title;
  const [, lead, word, rest] = match;
  const lower = word.toLowerCase();
  if (!VERBS.has(lower)) return title;
  const out = past(lower);
  const cased = word[0] === word[0].toUpperCase() ? out[0].toUpperCase() + out.slice(1) : out;
  return lead + cased + rest;
}
